import Quill from 'quill';
import 'quill/dist/quill.bubble.css';

/**
 * TextOverlayManager - Manages detached HTML text editors for JointJS shapes
 *
 * Instead of embedding Quill editors inside SVG foreignObjects (which causes
 * visibility, z-index, and initialization issues), this manager creates
 * regular DOM elements positioned above the SVG canvas.
 *
 * Features:
 * - Double-click to edit (prevents interference with shape interactions)
 * - Auto-sync position on shape move, pan, and zoom
 * - Proper cleanup on shape deletion
 */
class TextOverlayManager {
    constructor(paper, paperScroller, graph, options = {}) {
        this.paper = paper;
        this.paperScroller = paperScroller;
        this.graph = graph;
        this.editors = new Map(); // shapeId -> { quill, wrapper, isEditing }
        this.container = null;
        this.activeEditor = null; // Currently focused editor
        this.readOnly = options.readOnly || false; // If true, text is display-only (for Simulator)

        this.init();
    }

    init() {
        // Create or find the overlay container
        this.container = document.getElementById('text-overlay-container');
        if (!this.container) {
            console.warn('TextOverlayManager: #text-overlay-container not found in DOM');
            return;
        }
        console.log('TextOverlayManager: initialized, container found');

        this.setupGraphListeners();
        this.setupPaperListeners();
        this.setupDocumentListeners();
    }

    /**
     * Convert JointJS model coordinates to screen coordinates
     * Accounts for zoom level and scroll position
     */
    toScreenCoords(x, y) {
        // Use the paper's localToClientPoint to get proper screen coordinates
        // This accounts for all transformations (zoom, pan, scroll)
        const clientPoint = this.paper.localToClientPoint({ x, y });

        // Get the text-overlay-container's position to make coordinates relative to it
        const containerRect = this.container.getBoundingClientRect();

        // The position needs to be relative to the text-overlay-container
        // which should be positioned identically to the paper container
        return {
            x: clientPoint.x - containerRect.left,
            y: clientPoint.y - containerRect.top
        };
    }

    /**
     * Create a Quill editor for a shape
     */
    createEditor(shape) {
        const id = shape.id;
        console.log('TextOverlayManager: createEditor called for shape', id, 'readOnly:', this.readOnly);

        if (this.editors.has(id)) {
            console.log('TextOverlayManager: editor already exists for', id);
            return this.editors.get(id);
        }

        if (!this.container) {
            console.error('TextOverlayManager: container is null, cannot create editor');
            return null;
        }

        // Create wrapper element
        const wrapper = document.createElement('div');
        wrapper.id = `text-editor-${id}`;
        wrapper.className = 'text-editor-wrapper';

        if (this.readOnly) {
            wrapper.classList.add('read-only');
            wrapper.style.pointerEvents = 'none'; // No interaction in read-only mode
        } else {
            wrapper.style.pointerEvents = 'auto'; // Allow clicks to activate edit mode
        }

        let cleanupDrag = null;
        let cleanupResize = null;

        // Only create drag handle in edit mode (Designer)
        if (!this.readOnly) {
            // Create drag handle
            const dragHandle = document.createElement('div');
            dragHandle.className = 'text-editor-drag-handle';
            dragHandle.innerHTML = '⋮⋮'; // Drag grip icon
            wrapper.appendChild(dragHandle);

            // Set up drag functionality on the handle (returns cleanup function)
            cleanupDrag = this.setupDragHandle(dragHandle, wrapper, shape);
        }

        // Create inner editor div
        const editorDiv = document.createElement('div');
        editorDiv.className = 'text-editor-content';
        wrapper.appendChild(editorDiv);

        // Only create resize handle in edit mode (Designer)
        if (!this.readOnly) {
            // Create resize handle
            const resizeHandle = document.createElement('div');
            resizeHandle.className = 'text-editor-resize-handle';
            wrapper.appendChild(resizeHandle);

            // Set up resize functionality
            cleanupResize = this.setupResizeHandle(resizeHandle, wrapper, editorDiv, shape);
        }

        this.container.appendChild(wrapper);
        console.log('TextOverlayManager: wrapper added to container');

        // Apply saved size if available
        const savedWidth = shape.get('labelWidth');
        const savedHeight = shape.get('labelHeight');
        if (savedWidth) {
            editorDiv.style.width = `${savedWidth}px`;
        }
        if (savedHeight) {
            editorDiv.style.minHeight = `${savedHeight}px`;
        }

        // Initialize Quill
        const quill = new Quill(editorDiv, {
            theme: 'bubble',
            placeholder: this.readOnly ? '' : 'Label...',
            readOnly: this.readOnly,
            modules: {
                toolbar: this.readOnly ? false : [
                    ['bold', 'italic', 'underline'],
                    [{ 'size': ['small', false, 'large'] }],
                    [{ 'color': [] }]
                ]
            }
        });

        // Load existing content from shape
        // Prefer Delta format if available (safer), fall back to HTML
        const savedDelta = shape.get('textDelta');
        const savedHtml = shape.attr('shapename/innerHTML') || shape.get('textContent');

        if (savedDelta) {
            // Use Delta format (preferred - preserves formatting exactly)
            quill.setContents(savedDelta);
        } else if (savedHtml && savedHtml !== '<p><br></p>') {
            // Fall back to HTML for backward compatibility
            quill.clipboard.dangerouslyPasteHTML(savedHtml);
        }

        // Ensure shapename/innerHTML is always initialized for backward compatibility
        // This is needed by StoichWizardPopup, PropertyPopup, and other components that read innerHTML
        if (!shape.attr('shapename/innerHTML')) {
            shape.attr('shapename/innerHTML', '<p><br></p>');
        }

        // Only set up editing features if not read-only
        if (!this.readOnly) {
            // Sync content changes back to shape
            quill.on('text-change', () => {
                // Store Delta format for accurate save/load (used by TextOverlayManager)
                const delta = quill.getContents();
                shape.set('textDelta', delta);

                // Also store HTML for backward compatibility with other components
                // (stream-to-excel, popups, custom-objects, etc. all read innerHTML)
                const html = quill.root.innerHTML;
                shape.attr('shapename/innerHTML', html);
                shape.set('textContent', html);

                // Auto-center: update position as text width changes
                this.updateEditorPosition(shape);
            });

            // Click on editor area to enter edit mode
            wrapper.addEventListener('click', (e) => {
                e.stopPropagation(); // Prevent click from reaching the paper/shapes
                this.enterEditMode(id);
            });

            // Handle blur to exit edit mode
            quill.root.addEventListener('blur', (e) => {
                // Small delay to allow toolbar clicks
                setTimeout(() => {
                    if (!wrapper.contains(document.activeElement)) {
                        this.exitEditMode(id);
                    }
                }, 200);
            });
        }

        const editorData = {
            quill,
            wrapper,
            editorDiv,
            isEditing: false,
            cleanupDrag,
            cleanupResize
        };
        this.editors.set(id, editorData);

        // Position the editor
        this.updateEditorPosition(shape);

        return editorData;
    }

    /**
     * Enter edit mode - add editing class and focus
     */
    enterEditMode(shapeId) {
        const editor = this.editors.get(shapeId);
        if (!editor) return;

        // Exit any other active editor first
        if (this.activeEditor && this.activeEditor !== shapeId) {
            this.exitEditMode(this.activeEditor);
        }

        editor.wrapper.classList.add('editing');
        editor.isEditing = true;
        this.activeEditor = shapeId;

        // Focus the editor
        editor.quill.focus();
    }

    /**
     * Exit edit mode - keep clickable but remove editing state
     */
    exitEditMode(shapeId) {
        const editor = this.editors.get(shapeId);
        if (!editor) return;

        // Keep pointerEvents: auto so the editor remains clickable
        editor.wrapper.classList.remove('editing');
        editor.isEditing = false;

        if (this.activeEditor === shapeId) {
            this.activeEditor = null;
        }
    }

    /**
     * Set up drag functionality for the label
     * Returns cleanup function to remove document event listeners
     */
    setupDragHandle(dragHandle, wrapper, shape) {
        let isDragging = false;
        let startX, startY;
        let startLeft, startTop;

        const onMouseDown = (e) => {
            e.preventDefault();
            e.stopPropagation();
            isDragging = true;
            startX = e.clientX;
            startY = e.clientY;
            startLeft = parseInt(wrapper.style.left) || 0;
            startTop = parseInt(wrapper.style.top) || 0;
            wrapper.classList.add('dragging');
            document.body.style.cursor = 'grabbing';
        };

        const onMouseMove = (e) => {
            if (!isDragging) return;
            e.preventDefault();

            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;

            wrapper.style.left = `${startLeft + deltaX}px`;
            wrapper.style.top = `${startTop + deltaY}px`;
        };

        const onMouseUp = () => {
            if (!isDragging) return;
            isDragging = false;
            wrapper.classList.remove('dragging');
            document.body.style.cursor = '';

            // Calculate and save the offset from the default position (center of shape)
            const bbox = shape.getBBox();
            const zoom = this.paperScroller.zoom();
            const centerX = bbox.x + bbox.width / 2;
            const pos = this.toScreenCoords(centerX, bbox.y);

            // Get the editor data to access editorDiv for width calculation
            const editor = this.editors.get(shape.id);
            const contentWidth = editor ? (editor.editorDiv.offsetWidth || 60) : 60;
            const dragHandleWidth = 20;
            // Scale leftOffset by zoom to match updateEditorPosition calculation
            const leftOffset = ((contentWidth / 2) + dragHandleWidth) * zoom;

            const defaultLeft = pos.x - leftOffset;
            const defaultTop = pos.y + (bbox.height * zoom) + 5;

            const currentLeft = parseInt(wrapper.style.left) || 0;
            const currentTop = parseInt(wrapper.style.top) || 0;

            // Store offset in model coordinates (divide by zoom to be zoom-independent)
            const offsetX = (currentLeft - defaultLeft) / zoom;
            const offsetY = (currentTop - defaultTop) / zoom;

            shape.set('labelOffsetX', offsetX);
            shape.set('labelOffsetY', offsetY);
        };

        dragHandle.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        // Return cleanup function
        return () => {
            dragHandle.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }

    /**
     * Set up resize functionality for the text editor
     * Returns cleanup function to remove document event listeners
     */
    setupResizeHandle(resizeHandle, wrapper, editorDiv, shape) {
        let isResizing = false;
        let startX, startY;
        let startWidth, startHeight;

        const onMouseDown = (e) => {
            e.preventDefault();
            e.stopPropagation();
            isResizing = true;
            startX = e.clientX;
            startY = e.clientY;
            startWidth = editorDiv.offsetWidth || 80;
            startHeight = editorDiv.offsetHeight || 20;
            wrapper.classList.add('resizing');
            document.body.style.cursor = 'nwse-resize';
        };

        const onMouseMove = (e) => {
            if (!isResizing) return;
            e.preventDefault();

            const deltaX = e.clientX - startX;
            const deltaY = e.clientY - startY;

            const newWidth = Math.max(50, startWidth + deltaX);
            const newHeight = Math.max(20, startHeight + deltaY);

            editorDiv.style.width = `${newWidth}px`;
            editorDiv.style.minHeight = `${newHeight}px`;
        };

        const onMouseUp = () => {
            if (!isResizing) return;
            isResizing = false;
            wrapper.classList.remove('resizing');
            document.body.style.cursor = '';

            // Save the size to the shape
            const width = editorDiv.offsetWidth;
            const height = editorDiv.offsetHeight;
            shape.set('labelWidth', width);
            shape.set('labelHeight', height);
        };

        resizeHandle.addEventListener('mousedown', onMouseDown);
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);

        // Return cleanup function
        return () => {
            resizeHandle.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };
    }

    /**
     * Update editor position to match shape location
     * Position is set to center of shape (CSS transform handles centering)
     */
    updateEditorPosition(shape) {
        const editor = this.editors.get(shape.id);
        if (!editor) return;

        const bbox = shape.getBBox();
        const zoom = this.paperScroller.zoom();
        // Get center of shape
        const centerX = bbox.x + bbox.width / 2;
        const pos = this.toScreenCoords(centerX, bbox.y);

        // Get saved offset (in model coordinates)
        const labelOffsetX = shape.get('labelOffsetX') || 0;
        const labelOffsetY = shape.get('labelOffsetY') || 0;

        // Position below the shape
        const defaultOffsetY = 5; // Gap between shape and editor

        // Calculate the width of the text content to center it properly
        // Since we apply transform: scale(zoom), the visual width will be contentWidth * zoom
        const contentWidth = editor.editorDiv.offsetWidth || 60;

        // In Designer mode (not readOnly), account for drag handle width
        let leftOffset;
        if (this.readOnly) {
            // Simulator: just center the content (scaled by zoom)
            leftOffset = (contentWidth / 2) * zoom;
        } else {
            // Designer: center the content, accounting for drag handle on the left (scaled by zoom)
            const dragHandleWidth = 20; // approximate width of drag handle
            leftOffset = ((contentWidth / 2) + dragHandleWidth) * zoom;
        }

        const left = pos.x + (labelOffsetX * zoom) - leftOffset;
        const top = pos.y + (bbox.height * zoom) + defaultOffsetY + (labelOffsetY * zoom);

        editor.wrapper.style.left = `${left}px`;
        editor.wrapper.style.top = `${top}px`;

        // Scale the entire wrapper with zoom (including text, padding, handles, etc.)
        editor.wrapper.style.transform = `scale(${zoom})`;
        editor.wrapper.style.transformOrigin = 'top left';
    }

    /**
     * Update all editor positions (called on pan/zoom)
     */
    updateAllPositions() {
        this.editors.forEach((editor, shapeId) => {
            const shape = this.graph.getCell(shapeId);
            if (shape) {
                this.updateEditorPosition(shape);
            }
        });
    }

    /**
     * Remove editor for a shape
     */
    removeEditor(shapeId) {
        const editor = this.editors.get(shapeId);
        if (editor) {
            if (this.activeEditor === shapeId) {
                this.activeEditor = null;
            }
            // Clean up document event listeners for drag
            if (editor.cleanupDrag) {
                editor.cleanupDrag();
            }
            // Clean up document event listeners for resize
            if (editor.cleanupResize) {
                editor.cleanupResize();
            }
            editor.wrapper.remove();
            this.editors.delete(shapeId);
        }
    }

    /**
     * Load content from shape data into editor
     */
    loadContent(shape) {
        const editor = this.editors.get(shape.id);
        if (!editor) return;

        // Prefer Delta format if available
        const savedDelta = shape.get('textDelta');
        const savedHtml = shape.attr('shapename/innerHTML') || shape.get('textContent');

        if (savedDelta) {
            editor.quill.setContents(savedDelta);
        } else if (savedHtml) {
            editor.quill.clipboard.dangerouslyPasteHTML(savedHtml);
        }
    }

    /**
     * Set up listeners for graph events
     */
    setupGraphListeners() {
        console.log('TextOverlayManager: setupGraphListeners called');

        // Create editor when new shape is added
        this.graph.on('add', (cell) => {
            console.log('TextOverlayManager: graph add event, cell id:', cell.id, 'type:', cell.get('type'), 'isElement:', cell.isElement());
            if (cell.isElement()) {
                const shape = cell;
                const shapeType = shape.get('type') || '';
                // Check isUnit property OR if it's a pfd/bfd shape type (isUnit gets set after add)
                const isUnit = shape.get('isUnit') === 'yes' ||
                              shape.prop('isUnit') === 'yes' ||
                              shapeType.includes('pfd') ||
                              shapeType.includes('bfd');
                console.log('TextOverlayManager: shape added, id:', shape.id, 'type:', shapeType, 'isUnit:', isUnit);
                if (isUnit) {
                    // Small delay to ensure shape is fully rendered and properties are set
                    setTimeout(() => {
                        // Double-check the shape still exists and we don't already have an editor
                        if (this.graph.getCell(shape.id) && !this.editors.has(shape.id)) {
                            this.createEditor(shape);
                        } else {
                            console.log('TextOverlayManager: skipping createEditor - shape removed or editor exists');
                        }
                    }, 100);
                }
            }
        });

        // Update position when shape moves
        this.graph.on('change:position', (shape) => {
            if (this.editors.has(shape.id)) {
                this.updateEditorPosition(shape);
            }
        });

        // Update position when shape resizes
        this.graph.on('change:size', (shape) => {
            if (this.editors.has(shape.id)) {
                this.updateEditorPosition(shape);
            }
        });

        // Remove editor when shape is removed
        this.graph.on('remove', (cell) => {
            console.log('TextOverlayManager: graph remove event, cell id:', cell.id);
            if (cell.isElement()) {
                this.removeEditor(cell.id);
            }
        });

        // Clear all editors when graph is reset
        this.graph.on('reset', () => {
            this.clearAllEditors();
        });
    }

    /**
     * Set up listeners for paper/scroller events
     */
    setupPaperListeners() {
        // Double-click on shape to edit
        this.paper.on('element:pointerdblclick', (cellView) => {
            const shape = cellView.model;

            // Only for shapes that should have text editors
            if (shape.get('isUnit') === 'yes' || shape.prop('isUnit') === 'yes') {
                // Create editor if it doesn't exist
                if (!this.editors.has(shape.id)) {
                    this.createEditor(shape);
                }
                this.enterEditMode(shape.id);
            }
        });

        // Click on blank area exits edit mode
        this.paper.on('blank:pointerdown', () => {
            if (this.activeEditor) {
                this.exitEditMode(this.activeEditor);
            }
        });

        // Update positions on scroll
        this.paperScroller.el.addEventListener('scroll', () => {
            this.updateAllPositions();
        });
    }

    /**
     * Set up document-level listeners
     */
    setupDocumentListeners() {
        // Exit edit mode when clicking outside
        document.addEventListener('mousedown', (e) => {
            if (this.activeEditor) {
                const editor = this.editors.get(this.activeEditor);
                if (editor && !editor.wrapper.contains(e.target)) {
                    // Check if click is on the paper/shape
                    const paperEl = this.paper.el;
                    if (!paperEl.contains(e.target)) {
                        this.exitEditMode(this.activeEditor);
                    }
                }
            }
        });

        // Handle escape key to exit edit mode
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.activeEditor) {
                this.exitEditMode(this.activeEditor);
            }
        });
    }

    /**
     * Initialize editors for all existing shapes (call after loading a graph)
     */
    initializeExistingShapes() {
        const shapes = this.graph.getElements();
        console.log('TextOverlayManager: initializeExistingShapes called, found', shapes.length, 'elements');
        shapes.forEach(shape => {
            // Create editor for all unit shapes (not just those with existing text)
            const isUnit = shape.get('isUnit') === 'yes' || shape.prop('isUnit') === 'yes';
            console.log('TextOverlayManager: shape', shape.id, 'isUnit:', isUnit);
            if (isUnit) {
                this.createEditor(shape);
            }
        });
        console.log('TextOverlayManager: editors created:', this.editors.size);
    }

    /**
     * Clear all editors
     */
    clearAllEditors() {
        this.editors.forEach((editor, shapeId) => {
            editor.wrapper.remove();
        });
        this.editors.clear();
        this.activeEditor = null;
    }

    /**
     * Handle zoom changes
     */
    onZoom() {
        this.updateAllPositions();
    }

    /**
     * Generate text labels as HTML elements for printing
     * Returns an array of positioned HTML elements to be added to the print area
     */
    getLabelsForPrint() {
        const labels = [];

        this.editors.forEach((editor, shapeId) => {
            const shape = this.graph.getCell(shapeId);
            if (!shape) return;

            // Get the HTML content from the Quill editor
            const html = editor.quill.root.innerHTML;

            // Skip if empty or just a blank paragraph
            if (!html || html === '<p><br></p>' || html.trim() === '') return;

            // Get shape bounding box in model coordinates
            const bbox = shape.getBBox();

            // Get saved offset (in model coordinates)
            const labelOffsetX = shape.get('labelOffsetX') || 0;
            const labelOffsetY = shape.get('labelOffsetY') || 0;

            // Position below the shape center
            const centerX = bbox.x + bbox.width / 2;
            const defaultOffsetY = 5; // Gap between shape and label

            // Calculate position in model coordinates
            const x = centerX + labelOffsetX;
            const y = bbox.y + bbox.height + defaultOffsetY + labelOffsetY;

            // Get saved dimensions or use defaults
            const width = shape.get('labelWidth') || editor.editorDiv.offsetWidth || 60;

            labels.push({
                html: html,
                x: x,
                y: y,
                width: width,
                shapeId: shapeId
            });
        });

        return labels;
    }

    /**
     * Destroy the manager and clean up
     */
    destroy() {
        this.clearAllEditors();
        // Remove event listeners would go here if we stored references
    }
}

export default TextOverlayManager;
